import { useMemo } from 'react';
import { AthleteProfile } from '../../../stores/v4/useAthleteStore';
import { PILLARS, NUTRIENT_REGISTRY, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';

export interface PillarMetric {
    id: string;
    label: string;
    value: number;
    target: number;
    unit: string;
    sources: any[];
}

export interface PillarSystem {
    title: string;
    color: string;
    metrics: PillarMetric[];
}

/**
 * useMetabolicMatrix: Strategic hook for mapping physiological domains.
 * V8.4: HARDENED. Uses NUTRIENT_REGISTRY for pretty labels.
 */
export const useMetabolicMatrix = (profile: AthleteProfile | null, consumed: any, targets: any) => {
    
    const SENSITIVITY_LABELS = {
        'HIGH': 'OPTIMAL',
        'NORMAL': 'NORMAL',
        'LOW': 'RESISTANT'
    };

    const thresholds = useMemo(() => {
        const sensitivity = profile?.insulin_sensitivity || 'NORMAL';
        if (sensitivity === 'HIGH') return { stable: 70, efficient: 180 };
        if (sensitivity === 'LOW') return { stable: 35, efficient: 120 };
        return { stable: 50, efficient: 150 };
    }, [profile]);

    const pillarData: PillarSystem[] = useMemo(() => {
        const m = consumed.micros || {};
        const t = targets.micros || {};
        const mt = targets.macros || {};

        return PILLARS.map(p => ({
            title: p.label,
            color: p.color,
            metrics: p.keys.map(key => {
                const data = m[key] || { total: 0, sources: [] };
                const lowKey = normalizeNutrientKey(key);
                const registryProfile = NUTRIENT_REGISTRY[lowKey];

                return {
                    id: key,
                    label: registryProfile?.label || key.replace(/_/g, ' ').toUpperCase(),
                    unit: registryProfile?.unit || (key === 'fiber' ? 'g' : 'mg'),
                    value: data.total,
                    target: key === 'fiber' ? (mt.fiber || 30) : (t[key] || 0),
                    sources: data.sources
                };
            })
        }));
    }, [consumed, targets]);

    return {
        thresholds,
        pillarData,
        sensitivityLabel: (SENSITIVITY_LABELS as any)[profile?.insulin_sensitivity || 'NORMAL']
    };
};
